import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import api from '../../../API';
import styles from '../GirderConnection.module.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const PAGE_SIZE = 50;
const DEFAULT_SORT = 'startedAt';
const DEFAULT_SORT_DIR = 'desc';
const SORTING_ORDER = ['asc', 'desc', null];
const OVERLAY_LOADING = '<span class="ag-overlay-loading-center">Loading…</span>';
const OVERLAY_NO_ROWS = '<span class="ag-overlay-loading-center">No sync operations found.</span>';

function formatTs(value) {
    if (!value) return '';
    try {
        return new Date(value).toLocaleString();
    } catch {
        return String(value);
    }
}

function formatDurationMs(ms) {
    if (ms == null || ms === '' || Number.isNaN(Number(ms))) return '';
    const totalSec = Math.max(0, Math.floor(Number(ms) / 1000));
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
    if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
    return `${s}s`;
}

function countsFromItem(item) {
    if (item.statsCreated != null || item.statsUpdated != null || item.statsDeleted != null) {
        return {
            created: item.statsCreated || 0,
            updated: item.statsUpdated || 0,
            deleted: item.statsDeleted || 0,
        };
    }
    const byNode = (item.statistics && item.statistics.byNode) || {};
    let created = 0;
    let updated = 0;
    let deleted = 0;
    Object.values(byNode).forEach((node) => {
        created += (node.foldersToAdd || 0) + (node.itemsToAdd || 0);
        updated += (node.foldersToUpdate || 0) + (node.itemsToUpdate || 0);
        deleted += (node.foldersToRemove || 0) + (node.itemsToRemove || 0);
    });
    return { created, updated, deleted };
}

function sanitizeRecordForDisplay(record, listRow) {
    const out = { ...(record || {}) };
    delete out.diffResult;
    if (listRow) {
        if (listRow.peer && !out.remoteDisplayName) {
            out.peerLabel = listRow.peer;
        }
    }
    return out;
}

/**
 * Map ag-grid text/number filter model to API query params.
 */
function filtersFromGridModel(model) {
    if (!model) return {};
    const out = {};
    const getNumber = (key, minKey, maxKey) => {
        const f = model[key];
        if (!f) return;
        if (f.type === 'equals' && f.filter != null) {
            out[minKey] = f.filter;
            out[maxKey] = f.filter;
        } else if (f.type === 'greaterThan' || f.type === 'greaterThanOrEqual') {
            out[minKey] = f.filter;
        } else if (f.type === 'lessThan' || f.type === 'lessThanOrEqual') {
            out[maxKey] = f.filter;
        } else if (f.type === 'inRange') {
            if (f.filter != null) out[minKey] = f.filter;
            if (f.filterTo != null) out[maxKey] = f.filterTo;
        } else if (f.filter != null) {
            out[minKey] = f.filter;
            out[maxKey] = f.filter;
        }
    };

    if (model.status?.filter) out.status = String(model.status.filter).trim();
    if (model.trigger?.filter) out.trigger = String(model.trigger.filter).trim();
    if (model.peer?.filter) out.peer = String(model.peer.filter).trim();
    if (model.error?.filter) out.error = String(model.error.filter).trim();
    getNumber('created', 'createdMin', 'createdMax');
    getNumber('updated', 'updatedMin', 'updatedMax');
    getNumber('deleted', 'deletedMin', 'deletedMax');
    getNumber('durationMs', 'durationMinMs', 'durationMaxMs');

    if (model.startedAt?.filter) {
        out.startedAfter = String(model.startedAt.filter).trim();
    }
    if (model.completedAt?.filter) {
        out.completedAfter = String(model.completedAt.filter).trim();
    }
    return out;
}

function applySortIndicator(gridApi, sortField, sortDir, showIndicator) {
    if (!gridApi) return;
    if (!showIndicator || !sortField || !sortDir) {
        gridApi.applyColumnState({
            defaultState: { sort: null },
        });
        return;
    }
    gridApi.applyColumnState({
        state: [{ colId: sortField, sort: sortDir }],
        defaultState: { sort: null },
    });
}

/**
 * Paginated sync history for one connection/node or all connections.
 */
export default function SyncHistoryModal({
    open,
    onClose,
    connectionId = null,
    nodeUuid = null,
    peerName = null,
}) {
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sort, setSort] = useState(DEFAULT_SORT);
    const [sortDir, setSortDir] = useState(DEFAULT_SORT_DIR);
    /** When false, API uses default startedAt desc but headers show no sort arrow. */
    const [sortIndicatorVisible, setSortIndicatorVisible] = useState(true);
    const [filterParams, setFilterParams] = useState({});
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [detailJson, setDetailJson] = useState(null);
    const gridRef = useRef(null);
    const suppressGridEventsRef = useRef(true);
    const sortRef = useRef(sort);
    const sortDirRef = useRef(sortDir);
    const sortIndicatorVisibleRef = useRef(sortIndicatorVisible);
    const filterParamsRef = useRef(filterParams);

    sortRef.current = sort;
    sortDirRef.current = sortDir;
    sortIndicatorVisibleRef.current = sortIndicatorVisible;
    filterParamsRef.current = filterParams;

    const title = connectionId
        ? `Sync history${peerName ? ` — ${peerName}` : ''}${nodeUuid ? ' (this node)' : ''}`
        : 'All sync history';

    const closeDetail = useCallback(() => {
        setDetailOpen(false);
        setDetailJson(null);
        setDetailError(null);
        setDetailLoading(false);
    }, []);

    const loadPage = useCallback(async (nextOffset, nextSort, nextDir, nextFilters, showIndicator = true) => {
        setLoading(true);
        setError(null);
        const s = nextSort ?? sortRef.current;
        const d = nextDir ?? sortDirRef.current;
        const f = nextFilters ?? filterParamsRef.current;
        const apiGrid = gridRef.current?.api;
        if (apiGrid) {
            apiGrid.showLoadingOverlay();
        }
        try {
            const query = {
                limit: PAGE_SIZE,
                offset: nextOffset,
                sort: s,
                sortDir: d,
                ...f,
            };
            if (connectionId) query.connectionId = connectionId;
            if (nodeUuid) query.nodeUuid = nodeUuid;
            Object.keys(query).forEach((k) => {
                if (query[k] == null || query[k] === '') delete query[k];
            });

            const response = await api.get('network/sync/records', { query });
            if (response.status !== 200) {
                const errBody = response.data;
                const message = (errBody && (errBody.message || errBody.error))
                    || `Request failed (${response.status})`;
                throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
            }
            const data = response.data || {};
            const items = data.items || [];
            const nextSortField = data.sort || s;
            const nextSortDirection = data.sortDir || d;
            suppressGridEventsRef.current = true;
            setRows(items.map((item) => {
                const totals = countsFromItem(item);
                return {
                    id: item._id,
                    startedAt: item.startedAt,
                    completedAt: item.completedAt,
                    durationMs: item.durationMs,
                    status: item.status || '',
                    trigger: item.trigger || '',
                    peer: item.remoteDisplayName
                        ? `${item.remoteDisplayName}${item.remoteUuid ? ` (${item.remoteUuid})` : ''}`
                        : (item.remoteUuid || ''),
                    created: totals.created,
                    updated: totals.updated,
                    deleted: totals.deleted,
                    error: item.error || '',
                };
            }));
            setTotal(data.total || 0);
            setOffset(data.offset ?? nextOffset);
            setSort(nextSortField);
            setSortDir(nextSortDirection);
            setSortIndicatorVisible(showIndicator);
            requestAnimationFrame(() => {
                applySortIndicator(
                    gridRef.current?.api,
                    nextSortField,
                    nextSortDirection,
                    showIndicator,
                );
                suppressGridEventsRef.current = false;
                const g = gridRef.current?.api;
                if (g) {
                    if (items.length === 0) g.showNoRowsOverlay();
                    else g.hideOverlay();
                }
            });
        } catch (err) {
            setError(err?.message || String(err));
            setRows([]);
            setTotal(0);
            suppressGridEventsRef.current = false;
            gridRef.current?.api?.showNoRowsOverlay();
        } finally {
            setLoading(false);
        }
    }, [connectionId, nodeUuid]);

    useEffect(() => {
        if (!open) {
            closeDetail();
            return undefined;
        }
        suppressGridEventsRef.current = true;
        setOffset(0);
        setSort(DEFAULT_SORT);
        setSortDir(DEFAULT_SORT_DIR);
        setSortIndicatorVisible(true);
        setFilterParams({});
        setRows([]);
        loadPage(0, DEFAULT_SORT, DEFAULT_SORT_DIR, {}, true);
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when opening / scope changes
    }, [open, connectionId, nodeUuid]);

    // Esc: close detail first, then history.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (detailOpen) {
                e.stopPropagation();
                closeDetail();
                return;
            }
            onClose();
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [open, detailOpen, closeDetail, onClose]);

    const onGridReady = useCallback((params) => {
        applySortIndicator(
            params.api,
            sortRef.current,
            sortDirRef.current,
            sortIndicatorVisibleRef.current,
        );
        if (loading) {
            params.api.showLoadingOverlay();
        }
    }, [loading]);

    const onSortChanged = useCallback(() => {
        if (suppressGridEventsRef.current) return;
        const gridApi = gridRef.current?.api;
        if (!gridApi) return;
        const state = gridApi.getColumnState().find((c) => c.sort);
        // Cleared: third click — no sort on any column
        if (!state?.colId || !state.sort) {
            setSort(DEFAULT_SORT);
            setSortDir(DEFAULT_SORT_DIR);
            setSortIndicatorVisible(false);
            loadPage(0, DEFAULT_SORT, DEFAULT_SORT_DIR, filterParamsRef.current, false);
            return;
        }
        const field = state.colId;
        const dir = state.sort === 'asc' ? 'asc' : 'desc';
        if (
            sortIndicatorVisibleRef.current
            && field === sortRef.current
            && dir === sortDirRef.current
        ) {
            return;
        }
        setSort(field);
        setSortDir(dir);
        setSortIndicatorVisible(true);
        loadPage(0, field, dir, filterParamsRef.current, true);
    }, [loadPage]);

    const onFilterChanged = useCallback(() => {
        if (suppressGridEventsRef.current) return;
        const gridApi = gridRef.current?.api;
        if (!gridApi) return;
        const model = gridApi.getFilterModel();
        const next = filtersFromGridModel(model);
        setFilterParams(next);
        loadPage(
            0,
            sortRef.current,
            sortDirRef.current,
            next,
            sortIndicatorVisibleRef.current,
        );
    }, [loadPage]);

    const onRowClicked = useCallback(async (event) => {
        const id = event?.data?.id;
        if (!id) return;
        // Ignore clicks that are part of header / filter interactions
        const target = event?.event?.target;
        if (target?.closest?.('.ag-header') || target?.closest?.('.ag-menu')) return;

        const listRow = event.data;
        setDetailOpen(true);
        setDetailLoading(true);
        setDetailError(null);
        setDetailJson(null);
        try {
            const response = await api.get(`network/sync/record/${id}`);
            if (response.status !== 200) {
                const errBody = response.data;
                const message = (errBody && (errBody.message || errBody.error))
                    || `Request failed (${response.status})`;
                throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
            }
            const sanitized = sanitizeRecordForDisplay(response.data, listRow);
            setDetailJson(JSON.stringify(sanitized, null, 2));
        } catch (err) {
            setDetailError(err?.message || String(err));
        } finally {
            setDetailLoading(false);
        }
    }, []);

    const columnDefs = useMemo(() => {
        const cols = [
            {
                field: 'startedAt',
                colId: 'startedAt',
                headerName: 'Started',
                flex: 1.2,
                minWidth: 150,
                filter: 'agTextColumnFilter',
                valueFormatter: (p) => formatTs(p.value),
            },
            {
                field: 'completedAt',
                colId: 'completedAt',
                headerName: 'Completed',
                flex: 1.2,
                minWidth: 150,
                filter: 'agTextColumnFilter',
                valueFormatter: (p) => formatTs(p.value),
            },
            {
                field: 'durationMs',
                colId: 'durationMs',
                headerName: 'Duration',
                flex: 0.8,
                minWidth: 100,
                filter: 'agNumberColumnFilter',
                valueFormatter: (p) => formatDurationMs(p.value),
            },
            {
                field: 'status',
                colId: 'status',
                headerName: 'Status',
                flex: 0.8,
                minWidth: 100,
                filter: 'agTextColumnFilter',
            },
            {
                field: 'trigger',
                colId: 'trigger',
                headerName: 'Type',
                flex: 0.6,
                minWidth: 80,
                filter: 'agTextColumnFilter',
            },
        ];
        if (!nodeUuid) {
            cols.push({
                field: 'peer',
                colId: 'peer',
                headerName: 'Peer',
                flex: 1.4,
                minWidth: 180,
                filter: 'agTextColumnFilter',
            });
        }
        cols.push(
            {
                field: 'created',
                colId: 'created',
                headerName: 'Created',
                flex: 0.6,
                minWidth: 90,
                filter: 'agNumberColumnFilter',
            },
            {
                field: 'updated',
                colId: 'updated',
                headerName: 'Updated',
                flex: 0.6,
                minWidth: 90,
                filter: 'agNumberColumnFilter',
            },
            {
                field: 'deleted',
                colId: 'deleted',
                headerName: 'Deleted',
                flex: 0.6,
                minWidth: 90,
                filter: 'agNumberColumnFilter',
            },
            {
                field: 'error',
                colId: 'error',
                headerName: 'Error',
                flex: 1.5,
                minWidth: 160,
                filter: 'agTextColumnFilter',
            },
        );
        return cols;
    }, [nodeUuid]);

    // Stable refs so detail open/close re-renders do not reset column widths.
    const defaultColDef = useMemo(() => ({
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: false,
        unSortIcon: true,
        comparator: () => 0,
    }), []);

    const getRowId = useCallback((params) => String(params.data.id), []);

    if (!open) return null;

    const pageStart = total === 0 ? 0 : offset + 1;
    const pageEnd = Math.min(offset + PAGE_SIZE, total);
    const canPrev = offset > 0 && !loading;
    const canNext = offset + PAGE_SIZE < total && !loading;

    // Portal to body so position:fixed is not trapped by .connection:hover transform
    // (that containing-block issue only covered the connection card).
    const modal = (
        <div
            className={styles.autoSyncDialogOverlay}
            onClick={onClose}
            role="presentation"
        >
            <div
                className={`${styles.autoSyncDialog} ${styles.syncHistoryDialog}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="sync-history-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.autoSyncDialogHeader}>
                    <h3 id="sync-history-title" className={styles.autoSyncDialogTitle}>
                        {title}
                    </h3>
                    <button
                        type="button"
                        className={styles.autoSyncDialogClose}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <div className={`${styles.autoSyncDialogBody} ${styles.syncHistoryDialogBody}`}>
                    {error && (
                        <div className={styles.inlineActionError}>{error}</div>
                    )}
                    <div className={`ag-theme-alpine ${styles.syncHistoryGrid}`}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={rows}
                            getRowId={getRowId}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            onGridReady={onGridReady}
                            onSortChanged={onSortChanged}
                            onFilterChanged={onFilterChanged}
                            onRowClicked={onRowClicked}
                            sortingOrder={SORTING_ORDER}
                            suppressMultiSort={true}
                            domLayout="normal"
                            animateRows={false}
                            suppressCellFocus={true}
                            overlayLoadingTemplate={OVERLAY_LOADING}
                            overlayNoRowsTemplate={OVERLAY_NO_ROWS}
                        />
                    </div>
                    <div className={styles.syncHistoryPager}>
                        <span className={styles.syncHistoryPagerLabel}>
                            {total === 0
                                ? '0 records'
                                : `${pageStart}–${pageEnd} of ${total}`}
                            {' · Click a row for full details'}
                        </span>
                        <div className={styles.syncHistoryPagerButtons}>
                            <button
                                type="button"
                                className={styles.syncButtonSecondary}
                                disabled={!canPrev}
                                onClick={() => loadPage(
                                    Math.max(0, offset - PAGE_SIZE),
                                    undefined,
                                    undefined,
                                    undefined,
                                    sortIndicatorVisible,
                                )}
                            >
                                Previous
                            </button>
                            <button
                                type="button"
                                className={styles.syncButtonSecondary}
                                disabled={!canNext}
                                onClick={() => loadPage(
                                    offset + PAGE_SIZE,
                                    undefined,
                                    undefined,
                                    undefined,
                                    sortIndicatorVisible,
                                )}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                {detailOpen && (
                    <div
                        className={styles.syncHistoryDetailOverlay}
                        onClick={closeDetail}
                        role="presentation"
                    >
                        <div
                            className={styles.syncHistoryDetailPanel}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="sync-record-detail-title"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className={styles.autoSyncDialogHeader}>
                                <h3
                                    id="sync-record-detail-title"
                                    className={styles.autoSyncDialogTitle}
                                >
                                    Sync record
                                </h3>
                                <button
                                    type="button"
                                    className={styles.autoSyncDialogClose}
                                    onClick={closeDetail}
                                    aria-label="Close detail"
                                >
                                    ×
                                </button>
                            </div>
                            <div className={styles.syncHistoryDetailBody}>
                                {detailLoading && <div>Loading…</div>}
                                {detailError && (
                                    <div className={styles.inlineActionError}>{detailError}</div>
                                )}
                                {!detailLoading && !detailError && detailJson && (
                                    <pre className={styles.syncHistoryDetailJson}>{detailJson}</pre>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}
